import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import {
  TourOverlay,
  applicableSteps,
  availableTourTargets,
  resumeIndex,
  type TourCapability,
} from "@playbookai/ui";
import type { OnboardingTourId } from "@playbookai/types";
import { tourDefinition, type OnboardingTours } from "./useOnboardingTours.js";

export interface OnboardingTourHostProps {
  readonly tours: OnboardingTours;
  readonly onOpenPlayerAI?: () => void;
  /** The capabilities this page can currently honour. */
  readonly capabilities: ReadonlySet<TourCapability>;
}

/** Human-readable tour names, for the dialog's accessible name. */
const TOUR_LABELS: Record<OnboardingTourId, string> = {
  home: "Home tour",
  "solve-owner": "SOLVE workspace tour",
  "solve-collaborator": "SOLVE collaboration tour",
  "solve-inviter": "SOLVE collaboration tour",
  vsm: "Value stream tour",
  hoshin: "Hoshin tour",
};

/**
 * Mounts the ONE tour renderer for whichever tour is active.
 *
 * Targets are resolved here rather than in the renderer because they are a
 * property of the page, and they are re-resolved on a bounded poll while the
 * tour is open: a workspace mounts progressively, so a target that is absent
 * at trigger time is routinely present a moment later. The poll is the only
 * thing that makes "wait for expected targets to finish loading, with bounded
 * waits" true without the renderer knowing what a SOLVE canvas is.
 */
export function OnboardingTourHost({
  tours,
  capabilities,
  onOpenPlayerAI,
}: OnboardingTourHostProps): ReactElement | null {
  const activeId = tours.active;
  const definition = activeId ? tourDefinition(activeId) : undefined;
  const [available, setAvailable] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const [index, setIndex] = useState(0);
  const [settled, setSettled] = useState(false);

  const declaredTargets = useMemo(
    () =>
      definition
        ? [
            ...new Set(
              definition.steps.flatMap((step) => [
                ...step.targets,
                ...(step.fallbackTargets ?? []),
              ]),
            ),
          ]
        : [],
    [definition],
  );

  useEffect(() => {
    if (!definition) {
      setSettled(false);
      return;
    }
    setSettled(false);
    let cancelled = false;
    const tick = (): void => {
      if (cancelled) return;
      const next = availableTourTargets(declaredTargets);
      setAvailable((previous) =>
        previous.size === next.size && [...next].every((id) => previous.has(id))
          ? previous
          : next,
      );
      setSettled(true);
      timer = window.setTimeout(tick, 500);
    };
    // Give the page one second to mount, then track targets while open.
    let timer = window.setTimeout(tick, 1000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [definition, declaredTargets]);

  const steps = useMemo(
    () =>
      definition
        ? applicableSteps(definition, {
            availableTargets: available,
            capabilities,
          })
        : [],
    [definition, available, capabilities],
  );

  // Seed the resume point ONCE per tour, from the server's record.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!activeId || !settled || steps.length === 0) return;
    if (seededFor.current === activeId) return;
    // Mark synchronously before updating state or recording progress: the
    // overlay measures in the commit phase and can flush this effect again
    // before a state-based guard has committed.
    seededFor.current = activeId;
    const resumedIndex = resumeIndex(
      steps,
      tours.progressFor(activeId)?.lastStepId,
    );
    setIndex(resumedIndex);
    tours.record(activeId, "in_progress", steps[resumedIndex]?.step.id ?? null);
  }, [activeId, settled, steps, tours]);

  useEffect(() => {
    if (activeId === null) seededFor.current = null;
  }, [activeId]);

  useEffect(() => {
    // Nothing applies here — no targets, or every step gated out. Exit without
    // recording anything: claiming somebody saw content that was never on the
    // page would be worse than never offering it.
    if (activeId && settled && steps.length === 0) tours.dismissActive();
  }, [activeId, settled, steps.length, tours]);

  const currentStepId = settled
    ? steps[Math.min(index, steps.length - 1)]?.step.id
    : undefined;
  const openedForStep = useRef<string | null>(null);
  useEffect(() => {
    const key =
      activeId && currentStepId === "player-ai"
        ? `${activeId}/${currentStepId}`
        : null;
    if (key && openedForStep.current !== key) {
      openedForStep.current = key;
      onOpenPlayerAI?.();
    } else if (!key) {
      openedForStep.current = null;
    }
  }, [activeId, currentStepId, onOpenPlayerAI]);

  if (!activeId || !definition || !settled || steps.length === 0) return null;
  const clamped = Math.min(index, steps.length - 1);

  return (
    <TourOverlay
      steps={steps}
      index={clamped}
      label={TOUR_LABELS[activeId]}
      onIndexChange={(next) => {
        setIndex(next);
        tours.record(activeId, "in_progress", steps[next]?.step.id ?? null);
      }}
      onFinish={() => {
        tours.record(activeId, "completed", null);
        tours.dismissActive();
      }}
      onSkip={() => {
        // Skip, the close control and Escape are ONE outcome, so a dismissed
        // tour stays dismissed instead of reappearing on the next visit.
        tours.record(activeId, "skipped", steps[clamped]?.step.id ?? null);
        tours.dismissActive();
      }}
    />
  );
}
